import { BaseType, Selection, select as d3Select } from "d3";

import { calculateLineCoordinates } from "@examspark/common/dist/utils/geom";
import { coordsAreEqual } from "@examspark/score";
import { Cartesian, Stem } from "@examspark/types";

import { WithIndex } from "../CartesianInequality";
import { D3Axis } from "../d3Utils";

import { useShapesDecorator } from "./drawShapes";

type DecorateInequalityQuadrantsProps = {
  line_1?: Cartesian.Shape.Line | Cartesian.Shape.DashedLine;
  line_2?: Cartesian.Shape.Line | Cartesian.Shape.DashedLine;
  x: D3Axis;
  y: D3Axis;
  selectedQuadrant?: number;
  mode?: string;
  onChange?: (answer: { inequality: Stem.Scoring["inequality"] }) => void;
};
export const decorateInequalityQuadrants =
  (props: DecorateInequalityQuadrantsProps) =>
  <GType extends BaseType>(graph: Selection<GType, any, any, any>) => {
    const decorateQuadrants = useShapesDecorator<BaseType | SVGGElement>({
      x: props.x,
      y: props.y,
    });

    const extraShapes = calculateQuadrants(props) || [];
    const quadrants = graph
      .selectAll("g.extra-shapes")
      .data([null])
      .join("g")
      .attr("class", "extra-shapes");

    const quadrant = quadrants
      .selectAll("g.extra-shape")
      .data<WithIndex<Cartesian.Shape>>(
        extraShapes.map((shape, index) => ({ ...shape, index })),
      )
      .join("g")
      .attr("class", "extra-shape")
      .call(decorateQuadrants);

    // const quadrant_shape =
    quadrant
      .selectAll("polygon")
      .data((d) => [d])
      .on("mouseover", function () {
        const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;
        if (d.index === props.selectedQuadrant) return;

        d3Select(this).attr("fill-opacity", 0.2);
      })
      .on("mouseout", function () {
        const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;
        if (d.index === props.selectedQuadrant) return;

        d3Select(this).attr("fill-opacity", 0);
      })
      .on("click", function () {
        if (props.mode === "answer") {
          const d = d3Select(this).datum() as WithIndex<Cartesian.Shape>;

          props.onChange?.({
            inequality: {
              quadrant: d.index,
            },
          });
        }
      })
      .attr("fill", (d, i) => "#1fa4e1")
      .attr("fill-opacity", (d) =>
        props.selectedQuadrant === d.index ? 0.4 : 0,
      )
      .attr("stroke-width", 0);

    // const quadrant_labels =
    // quadrant
    //   .selectAll("text.shape-label")
    //   .data((d) => [d])
    //   .join("text")
    //   .attr("class", "shape-label")
    //   .attr("text-anchor", "middle")
    //   .attr("dominant-baseline", "middle")
    //   .attr("font-size", 36)
    //   .attr("fill", "#000")
    //   .attr("x", (d) =>
    //     x.scale(
    //       "coord" in d
    //         ? d.coord.x
    //         : d.coords.reduce((acc, coord) => acc + coord.x, 0) /
    //             d.coords.length
    //     )
    //   )
    //   .attr("y", (d) =>
    //     y.scale(
    //       "coord" in d
    //         ? d.coord.y
    //         : d.coords.reduce((acc, coord) => acc + coord.y, 0) /
    //             d.coords.length
    //     )
    //   )
    //   .text((d, i) => d.index + 1);

    quadrant
      .selectAll(".shape-coords, .shape-coord-center")
      .style("opacity", 0);
  };

// 2 lines interseting will create 4 quadrants on the graph.
export const calculateQuadrants = (props: {
  line_1?: Cartesian.Shape.Line | Cartesian.Shape.DashedLine;
  line_2?: Cartesian.Shape.Line | Cartesian.Shape.DashedLine;
  x: D3Axis;
  y: D3Axis;
}) => {
  if (!props.line_1) return undefined;

  // if there's only one line, handle it separately
  if (!props.line_2) {
    const line_coords = calculateLineCoordinates({
      shape: props.line_1,
      x: props.x,
      y: props.y,
    });

    if (!line_coords) return undefined;

    // use our calculated coordinate groups to create the polygons themselves
    const quadrants = calculatePolygonShapes({
      line_coords,
      x: props.x,
      y: props.y,
    });

    return quadrants;
  } else {
    // one of the coordinates of each quadrant will be the intercept
    const intercept = calculateIntercept(props.line_1, props.line_2);

    // ensure we have an intercept within the bounds of the graph
    if (
      !intercept ||
      props.x.min > intercept.x ||
      intercept.x > props.x.max ||
      props.y.min > intercept.y ||
      intercept.y > props.y.max
    )
      return undefined;

    // two other coordinates will be where the lines intersect the edge of the
    // graph
    const line_1_coords = calculateLineCoordinates({
      shape: props.line_1,
      x: props.x,
      y: props.y,
    });

    const line_2_coords = calculateLineCoordinates({
      shape: props.line_2,
      x: props.x,
      y: props.y,
    });

    if (!line_1_coords || !line_2_coords) return undefined;

    const line_coords = [line_1_coords, line_2_coords].flat();

    const quadrants = calculatePolygonShapes({
      intercept,
      line_coords,
      x: props.x,
      y: props.y,
    });

    return quadrants;
  }
};

// formula stolen shamefully from
// https://www.topcoder.com/thrive/articles/Geometry%20Concepts%20part%202:%20%20Line%20Intersection%20and%20its%20Applications
const calculateIntercept = (
  line_1: Cartesian.Shape.Line | Cartesian.Shape.DashedLine,
  line_2: Cartesian.Shape.Line | Cartesian.Shape.DashedLine,
) => {
  const x1_1 = line_1.coords[0].x;
  const x2_1 = line_1.coords[1].x;
  const y1_1 = line_1.coords[0].y;
  const y2_1 = line_1.coords[1].y;

  const A_1 = y2_1 - y1_1;
  const B_1 = x1_1 - x2_1;
  const C_1 = A_1 * x1_1 + B_1 * y1_1;

  const x1_2 = line_2.coords[0].x;
  const x2_2 = line_2.coords[1].x;
  const y1_2 = line_2.coords[0].y;
  const y2_2 = line_2.coords[1].y;

  const A_2 = y2_2 - y1_2;
  const B_2 = x1_2 - x2_2;
  const C_2 = A_2 * x1_2 + B_2 * y1_2;

  const det = A_1 * B_2 - A_2 * B_1;

  if (det === 0) {
    return undefined;
  }

  const x = (B_2 * C_1 - B_1 * C_2) / det;
  const y = (A_1 * C_2 - A_2 * C_1) / det;

  return { x, y };
};

const uniqCoords = (
  acc: Cartesian.Coordinate[],
  coord: Cartesian.Coordinate,
) => {
  if (!acc.find((c) => coordsAreEqual(c, coord))) {
    acc.push(coord);
  }
  return acc;
};

const calculatePolygonShapes = ({
  intercept,
  line_coords,
  x,
  y,
}: {
  intercept?: Cartesian.Coordinate;
  line_coords: Cartesian.Coordinate[];
  x: D3Axis;
  y: D3Axis;
}) => {
  const graph_corners = [
    { x: x.max, y: y.max },
    { x: x.max, y: y.min },
    { x: x.min, y: y.min },
    { x: x.min, y: y.max },
  ];

  // partition the line coordinates into the 4 sides of the graph
  const top_coords = line_coords.filter((c) => c.y === y.max);
  const right_coords = line_coords.filter((c) => c.x === x.max);
  const bottom_coords = line_coords.filter((c) => c.y === y.min);
  const left_coords = line_coords.filter((c) => c.x === x.min);

  // sort the coordinates on each side of the graph to be clockwise around the graph
  top_coords.sort((a, b) => a.x - b.x);
  right_coords.sort((a, b) => a.y - b.y).reverse();
  bottom_coords.sort((a, b) => a.x - b.x).reverse();
  left_coords.sort((a, b) => a.y - b.y);

  // organize the points into a single array, going clockwise around the graph
  const points_to_divide = [
    ...top_coords,
    graph_corners[0],
    ...right_coords,
    graph_corners[1],
    ...bottom_coords,
    graph_corners[2],
    ...left_coords,
    graph_corners[3],
  ];

  // sort our line coordinates clockwise around the graph,
  // starting with the top left corner
  const clockwise_line_coordinates = [
    ...top_coords,
    ...right_coords,
    ...bottom_coords,
    ...left_coords,
  ]
    // if we hit the corner, we will have a duplicate that needs removed
    .reduce(uniqCoords, []);

  // figure out where the line coordinates are in the points array
  const point_indexes = clockwise_line_coordinates.map((coord) =>
    points_to_divide.findIndex((c) => coordsAreEqual(c, coord)),
  );

  // to get the polygons themselves, we divide the points into groups,
  // each group being the points between 2 of the line coordinates,
  // plus the intercept to complete our polygon
  const polygons: Cartesian.Shape.Polygon[] = point_indexes.map((index, i) => {
    const next_index = point_indexes[i + 1]
      ? point_indexes[i + 1] + 1
      : undefined;

    return {
      type: "polygon",
      coords: [
        // on 2-line graphs, we need to add the intercept as a coordinate for every polygon
        ...(intercept ? [intercept] : []),

        ...points_to_divide.slice(index, next_index),

        // quadrant 4 also needs to circle back and get any points we skipped
        // (such as when there are no top coordinates before the first right corner)
        ...(next_index === undefined
          ? points_to_divide.slice(0, point_indexes[0] + 1)
          : []),
      ].reduce(uniqCoords, []),
    };
  });

  return polygons;
};
